import React from 'react';
import { Box, Container, Typography, Grid, Paper, Stack, Chip, Button, Divider, Fade } from '@mui/material';
import { LocalShipping, Home as HomeIcon, Business, Inventory, Handyman, CleaningServices, Warehouse, WorkspacePremium, Speed, Shield, ThumbUp, Calculate } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const Services = () => {
  const navigate = useNavigate();

  const highlights = ['Festpreis möglich', 'Kurzfristige Termine', 'Voll versichert', 'Deutschlandweit'];

  const serviceItems = [
    {
      icon: <HomeIcon sx={{ fontSize: 36, color: '#275664' }} />,
      title: 'Privatumzüge',
      desc: 'Sorgfältige, stressfreie Umzüge für Wohnung und Haus – wir kümmern uns um alles.',
      bullets: ['Kostenlose Besichtigung', 'Demontage & Montage', 'Sicherer Transport', 'Tragen bis in die Wohnung']
    },
    {
      icon: <Business sx={{ fontSize: 36, color: '#275664' }} />,
      title: 'Firmenumzüge',
      desc: 'Planbare, zügige Betriebs- und Büroverlegungen mit minimaler Ausfallzeit.',
      bullets: ['Wochenend-/Nachtumzüge', 'IT & Arbeitsplatz-Umzug', 'Projektleitung', 'Schnelle Wiederaufnahme']
    },
    {
      icon: <Inventory sx={{ fontSize: 36, color: '#275664' }} />,
      title: 'Packservice',
      desc: 'Professionelles Verpacken mit geeignetem Material – sicher und geordnet.',
      bullets: ['Material inkl. verfügbar', 'Fachgerechte Polsterung', 'Beschriftungssystem', 'Auspackservice']
    },
    {
      icon: <LocalShipping sx={{ fontSize: 36, color: '#275664' }} />,
      title: 'Transportservice',
      desc: 'Zuverlässiger Transport mit modernem Fuhrpark und geschultem Team.',
      bullets: ['Express möglich', 'Versichert', 'Hebebühne & Equipment', 'Deutschlandweit']
    },
    {
      icon: <Handyman sx={{ fontSize: 36, color: '#275664' }} />,
      title: 'Möbelmontage',
      desc: 'Auf- und Abbau, Küchen- und Möbelmontage – passgenau und fachgerecht.',
      bullets: ['Küchen- & Möbelmontage', 'Anpassungen vor Ort', 'Auf-/Abbau', 'Fachgerechte Entsorgung']
    },
    {
      icon: <CleaningServices sx={{ fontSize: 36, color: '#275664' }} />,
      title: 'Entrümpelung',
      desc: 'Schnell und diskret – besenreine Übergabe inklusive Entsorgung.',
      bullets: ['Wohnungen & Keller', 'Nachlassauflösung', 'Besenreine Übergabe', 'Entsorgung inkl.']
    },
    {
      icon: <Warehouse sx={{ fontSize: 36, color: '#275664' }} />,
      title: 'Einlagerung',
      desc: 'Flexible Lagerflächen für kurz- und langfristige Einlagerung.',
      bullets: ['Kurz- & Langzeit', 'Sichere Lagerflächen', 'Versichert', 'Flexible Laufzeiten']
    },
    {
      icon: <Shield sx={{ fontSize: 36, color: '#275664' }} />,
      title: 'Spezialtransporte',
      desc: 'Klavier, Tresor, Antikmöbel – mit Schutzverpackung und Spezial-Equipment.',
      bullets: ['Geschulte Teams', 'Schutzverpackung', 'Spezial-Equipment', 'Sicher & zuverlässig']
    }
  ];

  const reasons = [
    { icon: <WorkspacePremium sx={{ color: '#ffd700' }} />, title: 'Qualität & Erfahrung', text: 'Langjährige Expertise, zertifizierte Abläufe und geschultes Personal.' },
    { icon: <Speed sx={{ color: '#55d6dc' }} />, title: 'Flexibel & pünktlich', text: 'Terminverbindlich, kurzfristig verfügbar und effizient im Ablauf.' },
    { icon: <Shield sx={{ color: '#55d6dc' }} />, title: 'Sicherheit', text: 'Versicherungsschutz und sorgfältiger Umgang mit Ihrem Eigentum.' },
    { icon: <ThumbUp sx={{ color: '#ffd700' }} />, title: 'Kundenzufriedenheit', text: 'Transparente Preise, klare Kommunikation und Top-Bewertungen.' }
  ];

  const steps = [
    { label: 'Anfrage & Beratung', text: 'Sie schildern Ihr Vorhaben – wir beraten telefonisch oder per E-Mail.' },
    { label: 'Besichtigung & Angebot', text: 'Vor Ort oder digital – wir erstellen ein transparentes Angebot.' },
    { label: 'Durchführung', text: 'Packen, Transport, Montage – professionell und termintreu.' },
    { label: 'Abnahme & Zufriedenheit', text: 'Gemeinsame Kontrolle, Übergabe und transparente Abrechnung.' }
  ];

  return (
    <Box sx={{ minHeight: '100vh', pt: 12, pb: 10 }}>
      {/* Hero */}
      <Box
        sx={{
          py: 8,
          background: 'linear-gradient(135deg, #0f2832 0%, #1a3a44 50%, #0f2832 100%)',
          borderBottom: '1px solid rgba(255,255,255,0.08)'
        }}
      >
        <Container maxWidth="lg">
          <Fade in timeout={700}>
            <Box>
              <Typography
                variant="h2"
                align="center"
                sx={{ fontWeight: 800, color: 'white', fontSize: { xs: '2.2rem', md: '3.2rem' }, letterSpacing: 0.3 }}
              >
                Leistungen, die bewegen
              </Typography>
              <Typography variant="h6" align="center" sx={{ mt: 2, color: '#55d6dc' }}>
                Umzüge, Montage, Packservice, Einlagerung und mehr – alles aus einer Hand.
              </Typography>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" justifyContent="center" sx={{ mt: 3 }}>
                {highlights.map((h) => (
                  <Chip
                    key={h}
                    label={h}
                    sx={{
                      bgcolor: 'rgba(255,255,255,0.06)',
                      color: 'white',
                      border: '1px solid rgba(255,255,255,0.12)'
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Services Grid */}
      <Container maxWidth="lg">
        <Box sx={{ py: 6 }}>
          <Typography variant="h4" align="center" sx={{ mb: 4, fontWeight: 'bold', color: 'white' }}>
            Unsere Services im Überblick
          </Typography>
          <Grid container spacing={3}>
            {serviceItems.map((s, idx) => (
              <Grid item xs={12} sm={6} md={4} key={s.title}>
                <Paper
                  sx={{
                    p: 3,
                    bgcolor: '#0f2832',
                    borderRadius: 3,
                    border: '1px solid rgba(255,255,255,0.08)',
                    position: 'relative',
                    overflow: 'hidden',
                    height: '100%',
                    transition: 'transform .25s ease, box-shadow .25s ease, border-color .25s ease',
                    '&:hover': {
                      transform: 'translateY(-6px)',
                      borderColor: '#55d6dc',
                      boxShadow: '0 10px 30px rgba(85,214,220,.12)'
                    }
                  }}
                >
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      background:
                        'radial-gradient(600px 120px at 20% -10%, rgba(85,214,220,.08), transparent 40%), radial-gradient(400px 120px at 110% 10%, rgba(255,215,0,.08), transparent 40%)',
                      pointerEvents: 'none'
                    }}
                  />
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box
                      sx={{
                        bgcolor: '#ffd700',
                        width: 56,
                        height: 56,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: '0 0 0 4px rgba(255,215,0,.15)'
                      }}
                    >
                      {s.icon}
                    </Box>
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
                      {s.title}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 1.5, color: 'white', opacity: 0.85 }}>
                    {s.desc}
                  </Typography>
                  <Stack component="ul" spacing={0.5} sx={{ mt: 2, pl: 2, m: 0 }}>
                    {s.bullets.map((b) => (
                      <Typography component="li" key={b} variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
                        {b}
                      </Typography>
                    ))}
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Box>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 4 }} />

        {/* Reasons and Process */}
        <Box sx={{ py: 4 }}>
          <Grid container spacing={4} alignItems="stretch">
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  bgcolor: '#1a3a44',
                  borderRadius: 3,
                  height: '100%',
                  border: '1px solid rgba(255,255,255,0.08)'
                }}
              >
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 800, mb: 2 }}>
                  Warum H&N Umzugsservice?
                </Typography>
                <Stack spacing={1.5}>
                  {reasons.map((r, i) => (
                    <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                      <Box sx={{ display: 'inline-flex', p: 1, bgcolor: 'rgba(255,255,255,0.06)', borderRadius: '50%' }}>
                        {r.icon}
                      </Box>
                      <Box>
                        <Typography sx={{ color: 'white', fontWeight: 700 }}>{r.title}</Typography>
                        <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>{r.text}</Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 3,
                  bgcolor: '#1a3a44',
                  borderRadius: 3,
                  height: '100%',
                  border: '1px solid rgba(255,255,255,0.08)'
                }}
              >
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 800, mb: 2 }}>
                  Ablauf
                </Typography>
                <Stack spacing={2}>
                  {steps.map((s, i) => (
                    <Stack key={i} direction="row" spacing={1.5} alignItems="flex-start">
                      <Box
                        sx={{
                          width: 30,
                          height: 30,
                          borderRadius: '50%',
                          bgcolor: '#ffd700',
                          color: '#275664',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 800,
                          flexShrink: 0,
                          mt: 0.3,
                          boxShadow: '0 0 0 4px rgba(255,215,0,.15)'
                        }}
                      >
                        {i + 1}
                      </Box>
                      <Box>
                        <Typography sx={{ color: 'white', fontWeight: 700 }}>{s.label}</Typography>
                        <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>{s.text}</Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* CTA */}
        <Box sx={{ mt: 6 }}>
          <Paper
            sx={{
              p: { xs: 3, md: 4 },
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              bgcolor: '#0f2832',
              border: '1px solid rgba(255,255,255,0.08)',
              borderRadius: 3,
              backgroundImage: 'linear-gradient(135deg, rgba(85,214,220,0.07), rgba(255,215,0,0.07))'
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <Box sx={{ p: 1, bgcolor: 'rgba(85,214,220,0.15)', borderRadius: '50%' }}>
                <Calculate sx={{ color: '#55d6dc' }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ color: 'white', fontWeight: 800 }}>
                  Sofort Preis einschätzen
                </Typography>
                <Typography variant="body2" sx={{ color: 'white', opacity: 0.8 }}>
                  Nutzen Sie den Kalkulator oder kontaktieren Sie uns direkt.
                </Typography>
              </Box>
            </Stack>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                variant="contained"
                onClick={() => navigate('/calculator')}
                sx={{ bgcolor: '#55d6dc', color: '#275664', fontWeight: 'bold', '&:hover': { bgcolor: '#4bc2c8' } }}
              >
                Zum Kalkulator
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate('/contact')}
                sx={{ borderColor: '#ffd700', color: '#ffd700', '&:hover': { bgcolor: 'rgba(255,215,0,.1)', borderColor: '#ffd700' } }}
              >
                Kontakt
              </Button>
            </Stack>
          </Paper>
        </Box>
      </Container>
    </Box>
  );
};

export default Services;